import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../helpers/firebase';
import firebaseLogo from '../../assets/images/firebase.svg';
import { generateCraftedAudioOnCall } from '../../helpers/API/content/googleFunctions';

const CurrentDayHealth = ({ 
  userId, 
  realityId, 
  currentDay, 
  onCreateCoreSession,
  onCreateCraftedSession,
  coreSessionLoading,
  craftedSessionLoading 
}) => {
  const [dayData, setDayData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatingAudio, setGeneratingAudio] = useState({});

  useEffect(() => {
    const fetchDayData = async () => {
      if (!userId || !realityId || !currentDay) return;

      try {
        setLoading(true);
        const dayRef = doc(db, `users/${userId}/realities/${realityId}/days/${currentDay}`);
        const daySnapshot = await getDoc(dayRef);

        if (daySnapshot.exists()) {
          setDayData(daySnapshot.data());
        }
      } catch (error) {
        console.error('Error fetching day data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDayData();
  }, [userId, realityId, currentDay]);

  const handleGenerateAudio = async (task) => {
    setGeneratingAudio(prev => ({ ...prev, [task.id]: true }));

    try {
      const exerciseData = {
        params: { userId, realityId, dayId: currentDay },
        data: {
          after: {
            data: {
              text: task.text,
              lang: task.lang,
              voice: task.voice,
              type: task.type,
              id: task.id,
            },
          },
        },
      };

      // Set a longer timeout for audio generation (2 minutes)
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('TIMEOUT')), 120000); // 2 minutes
      });

      await Promise.race([
        generateCraftedAudioOnCall({ exerciseData }),
        timeoutPromise
      ]);

      // Refresh day data after successful generation
      const dayRef = doc(db, `users/${userId}/realities/${realityId}/days/${currentDay}`);
      const daySnapshot = await getDoc(dayRef);
      if (daySnapshot.exists()) {
        setDayData(daySnapshot.data());
      }
    } catch (error) {
      console.error('Error generating audio:', error);

      if (error.message === 'TIMEOUT') {
        // For timeout, show a different message and keep checking
        alert('Audio generation is taking longer than expected. It may still be processing in the background. Please refresh the page in a few minutes to check if the audio was generated.');

        // Optional: Start polling to check if audio was generated
        setTimeout(() => {
          const dayRef = doc(db, `users/${userId}/realities/${realityId}/days/${currentDay}`);
          getDoc(dayRef).then(daySnapshot => {
            if (daySnapshot.exists()) {
              setDayData(daySnapshot.data());
            }
          });
        }, 30000); // Check again in 30 seconds
      } else {
        alert('Failed to generate audio. Please try again.');
      }
    } finally {
      setGeneratingAudio(prev => ({ ...prev, [task.id]: false }));
    }
  };

  const getExerciseTypeIcon = (type) => {
    switch (type) {
      case 'training':
        return '🏋️';
      case 'theory':
        return '📚';
      case 'crafted':
        return '✨';
      default:
        return '📝';
    }
  };

  const getExerciseTypeName = (type) => {
    switch (type) {
      case 'training':
        return 'Training';
      case 'theory':
        return 'Theory';
      case 'crafted':
        return 'Crafted';
      case 'core':
        return 'Core';
      default:
        return type;
    }
  };

  // Health check logic to detect missing sessions
  const checkDayHealth = () => {
    if (!dayData || !dayData.tasks) return [];
    
    const issues = [];
    const tasks = dayData.tasks;
    
    // Check for missing crafted session (should exist every day)
    const craftedTask = tasks.find(task => task.type === 'crafted');
    if (!craftedTask) {
      issues.push({
        type: 'crafted',
        message: 'Missing crafted session',
        action: 'create_crafted_session'
      });
    }
    
    // Check for missing core session on special days (1, 11, 22, etc.)
    const coreSessionDays = [1, 11, 22, 33, 44, 55, 66, 77, 88, 99];
    if (coreSessionDays.includes(currentDay)) {
      const coreTask = tasks.find(task => task.type === 'core');
      if (!coreTask) {
        issues.push({
          type: 'core',
          message: `Missing core session for day ${currentDay}`,
          action: 'create_core_session'
        });
      } else {
        // Check if core session has text or audio missing
        const hasText = coreTask.text && Array.isArray(coreTask.text) && coreTask.text.length > 0;
        const hasAudio = coreTask.audios && coreTask.audios[coreTask.voice] && coreTask.audios[coreTask.voice].audioURL;
        
        if (!hasText || !hasAudio) {
          issues.push({
            type: 'core',
            message: `Core session missing ${!hasText ? 'text' : 'audio'} for day ${currentDay}`,
            action: 'create_core_session'
          });
        }
      }
    }
    
    // Check for missing theory session (should exist every day)
    const hasTheory = tasks.some(task => task.type === 'theory');
    if (!hasTheory) {
      issues.push({
        type: 'theory',
        message: 'Missing theory session',
        action: 'verify_theory'
      });
    }
    
    return issues;
  };

  const handleFixIssue = async (issue) => {
    if (!onCreateCoreSession || !onCreateCraftedSession) return;
    
    try {
      switch (issue.action) {
        case 'create_core_session':
          await onCreateCoreSession(realityId, currentDay);
          break;
        case 'create_crafted_session':
          await onCreateCraftedSession(realityId, currentDay, true);
          break;
        default:
          console.warn(`Unknown action: ${issue.action}`);
      }
    } catch (error) {
      console.error(`Failed to fix ${issue.type}:`, error);
    }
  };

  if (loading) {
    return (
      <div className="current-day-health">
        <div className="health-header">
          <h4>Current Day {currentDay} Exercise Health</h4>
        </div>
        <div className="health-loading">
          <div className="spinner-border spinner-border-sm" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <span>Loading exercises...</span>
        </div>
      </div>
    );
  }

  if (!dayData || !dayData.tasks || dayData.tasks.length === 0) {
    return (
      <div className="current-day-health">
        <div className="health-header">
          <h4>Current Day {currentDay} Exercise Health</h4>
        </div>
        <div className="health-error">
          <i className="fas fa-exclamation-triangle"></i>
          <span>No exercises found for this day</span>
        </div>
      </div>
    );
  }

  const currentTaskId = dayData.currentTaskId || 1;

  return (
    <div className="current-day-health">
      <div className="health-header">
        <h4>Current Day {currentDay} Exercise Health</h4>
        <a
          href={`https://console.firebase.google.com/project/nevilize-prod/firestore/data/~2Fusers~2F${userId}~2Frealities~2F${realityId}~2Fdays~2F${currentDay}`}
          target="_blank"
          rel="noopener noreferrer"
          className="firebase-link"
          title="Open in Firebase Console"
        >
          <img
            src={firebaseLogo}
            alt="Firebase"
            className="firebase-icon"
          />
        </a>
      </div>

      <div className="exercises-grid">
        {dayData.tasks.map((task, index) => {
          const hasText = task.text && Array.isArray(task.text) && task.text.length > 0;
          const hasAudio = task.audios && task.audios[task.voice] && task.audios[task.voice].audioURL;
          const isCurrentTask = index === currentTaskId;
          const isGenerating = generatingAudio[task.id];

          return (
            <div
              key={task.id}
              className={`exercise-column ${isCurrentTask ? 'current-task' : ''}`}
            >
              {/* Exercise Header */}
              <div className="exercise-header">
                <span className="exercise-icon">{getExerciseTypeIcon(task.type)}</span>
                <div className="exercise-details">
                  <span className="exercise-id">Exercise {index + 1}</span>
                  <span className="exercise-type">{getExerciseTypeName(task.type)}</span>
                  {task.title && <span className="exercise-title">{task.title}</span>}
                </div>
                {isCurrentTask && (
                  <span className="current-badge">Current</span>
                )}
              </div>

              {/* Status Grid - Text and Audio stacked */}
              <div className="status-grid">
                <div className="status-card text-status">
                  <div className="status-header">
                    <span className="status-label">Text:</span>
                    <span className={`status-icon ${hasText ? 'success' : 'error'}`}>
                      {hasText ? '✓' : '✗'}
                    </span>
                  </div>
                </div>

                <div className="status-card audio-status">
                  <div className="status-header">
                    <span className="status-label">Audio:</span>
                    <span className={`status-icon ${hasAudio ? 'success' : 'error'}`}>
                      {hasAudio ? '✓' : '✗'}
                    </span>
                  </div>
                  {hasText && !hasAudio && (
                    <button
                      className="generate-audio-btn"
                      onClick={() => handleGenerateAudio(task)}
                      disabled={isGenerating}
                    >
                      {isGenerating ? (
                        <>
                          <div className="spinner-border spinner-border-sm" role="status">
                            <span className="sr-only">Loading...</span>
                          </div>
                          Generating...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-volume-up"></i>
                          Generate Audio
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>

              {/* Firebase Link */}
              <div className="firebase-link-container">
                <a
                  href={`https://console.firebase.google.com/project/nevilize-prod/firestore/data/~2Fusers~2F${userId}~2Frealities~2F${realityId}~2Fdays~2F${currentDay}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="exercise-firebase-link"
                  title="Open exercise in Firebase"
                >
                  <i className="fas fa-external-link-alt"></i>
                </a>
              </div>
            </div>
          );
        })}
      </div>

      {/* Health Check Section */}
      {checkDayHealth().length > 0 && (
        <div className="day-health-issues">
          <div className="health-issues-header">
            <h5>
              <i className="fas fa-exclamation-triangle"></i>
              Missing Sessions Detected
            </h5>
          </div>
          <div className="health-issues-list">
            {checkDayHealth().map((issue, index) => (
              <div key={index} className="health-issue">
                <div className="issue-info">
                  <span className="issue-message">{issue.message}</span>
                </div>
                <div className="issue-actions">
                  {issue.action === 'create_core_session' && (
                    <button
                      onClick={() => handleFixIssue(issue)}
                      disabled={coreSessionLoading && coreSessionLoading[`${realityId}-${currentDay}`]}
                      className="fix-issue-btn core-btn"
                    >
                      {coreSessionLoading && coreSessionLoading[`${realityId}-${currentDay}`] ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          Creating...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-brain"></i>
                          Create Core Session
                        </>
                      )}
                    </button>
                  )}
                  {issue.action === 'create_crafted_session' && (
                    <button
                      onClick={() => handleFixIssue(issue)}
                      disabled={craftedSessionLoading && craftedSessionLoading[`${realityId}-${currentDay}`]}
                      className="fix-issue-btn crafted-btn"
                    >
                      {craftedSessionLoading && craftedSessionLoading[`${realityId}-${currentDay}`] ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          Creating...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-magic"></i>
                          Create Crafted Session
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="health-summary">
        <div className="summary-stats">
          <span className="stat">
            <strong>{dayData.tasks.filter(t => t.text && t.text.length > 0).length}</strong>
            /{dayData.tasks.length} have text
          </span>
          <span className="stat">
            <strong>{dayData.tasks.filter(t => t.audios && t.audios[t.voice] && t.audios[t.voice].audioURL).length}</strong>
            /{dayData.tasks.length} have audio
          </span>
        </div>
      </div>
    </div>
  );
};

export default CurrentDayHealth;